import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersApi, adminApi } from '../services/api';
import { useLookups } from '../context/LookupContext';
import TopNav from '../components/layout/TopNav';

export default function NewOrderPage() {
  const navigate = useNavigate();
  const { lookups, lookupId, activeLoanTypes } = useLookups();
  const [users, setUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Default fileTypeId to 'Loan' once lookups are available
  const defaultFileTypeId = lookupId('fileTypes', 'Loan') || '';

  const [form, setForm] = useState({
    fileTypeId: '', loanNumber: '', origSystemId: '', loanTypeId: '',
    loanProgram: '', loanAmount: '', lender: '', broker: '', clientContact: '',
    propertyType: '', rate: '', jumbo: '', mi: '', lienStatusId: '', purpose: '',
    street: '', city: '', state: '', zip: '',
    assignedToId: '', closingDate: '',
    revenue: '', revReceived: false, revDate: '',
  });
  const [borrowers, setBorrowers] = useState([{ firstName: '', lastName: '', phone: '', email: '' }]);

  // Set default fileTypeId once lookups load
  useEffect(() => {
    if (defaultFileTypeId && !form.fileTypeId) {
      setForm(f => ({ ...f, fileTypeId: defaultFileTypeId }));
    }
  }, [defaultFileTypeId]);

  useEffect(() => {
    adminApi.getUsers().then(u => setUsers(u.filter(x => x.isActive))).catch(console.error);
  }, []);

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  function addBorrower() { setBorrowers(p => [...p, { firstName: '', lastName: '', phone: '', email: '' }]); }
  function removeBorrower(i) { if (borrowers.length > 1) setBorrowers(p => p.filter((_, idx) => idx !== i)); }
  function updateBorrower(i, field, val) { setBorrowers(p => p.map((b, idx) => idx === i ? { ...b, [field]: val } : b)); }

  const loanFileTypeId = lookupId('fileTypes', 'Loan');
  const isLoan = parseInt(form.fileTypeId) === loanFileTypeId;

  async function handleSubmit() {
    if (!form.fileTypeId || !form.loanNumber || !form.lender) {
      alert('Please fill in all required (*) fields.'); return;
    }
    const primary = borrowers[0];
    if (!primary?.firstName || !primary?.lastName) {
      alert('At least one borrower with first and last name is required.'); return;
    }

    setSubmitting(true);
    try {
      const result = await ordersApi.create({
        fileTypeId:    parseInt(form.fileTypeId),
        loanNumber:    form.loanNumber    || null,
        origSystemId:  form.origSystemId  ? parseInt(form.origSystemId)  : null,
        loanTypeId:    form.loanTypeId    ? parseInt(form.loanTypeId)    : null,
        loanProgram:   form.loanProgram   || null,
        loanAmount:    form.loanAmount    ? parseFloat(form.loanAmount)  : null,
        lender:        form.lender        || null,
        broker:        form.broker        || null,
        clientContact: form.clientContact || null,
        propertyType:  form.propertyType  || null,
        rate:          isLoan ? (form.rate ? parseFloat(form.rate) : null) : null,
        jumbo:         isLoan ? (form.jumbo === 'Yes' ? true : form.jumbo === 'No' ? false : null) : null,
        mi:            isLoan ? (form.mi   === 'Yes' ? true : form.mi   === 'No' ? false : null) : null,
        lienStatusId:  isLoan ? (form.lienStatusId ? parseInt(form.lienStatusId) : null) : null,
        purpose:       isLoan ? (form.purpose || null) : null,
        street:        form.street  || null,
        city:          form.city    || null,
        state:         form.state   || null,
        zip:           form.zip     || null,
        assignedToId:  form.assignedToId ? parseInt(form.assignedToId) : null,
        closingDate:   form.closingDate  || null,
        revenue:       form.revenue ? parseFloat(form.revenue) : null,
        revReceived:   form.revReceived,
        revDate:       form.revDate || null,
        borrowers:     borrowers.filter(b => b.firstName || b.lastName).map((b, i) => ({ ...b, sortOrder: i })),
      });
      navigate(`/orders/${result.id}`);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create file. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <TopNav />
      <div className="page-content">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
          <button className="btn btn-sm" onClick={() => navigate(-1)}>← Back</button>
          <h2>New file</h2>
        </div>

        {/* FILE INFORMATION */}
        <div className="card">
          <div className="section-hdr">File information</div>
          <div className="row5" style={{ marginBottom: 9 }}>
            <div className="fg"><label>File number</label><input readOnly value="Auto" /></div>
            <div className="fg"><label>File type *</label>
              <select value={form.fileTypeId} onChange={e => set('fileTypeId', e.target.value)}>
                <option value="">Select...</option>
                {lookups.fileTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="fg"><label>Date ordered</label><input readOnly value={new Date().toLocaleDateString('en-US')} /></div>
            <div className="fg"><label>Status</label><input readOnly value="Open" /></div>
            <div className="fg"><label>Assigned to</label>
              <select value={form.assignedToId} onChange={e => set('assignedToId', e.target.value)}>
                <option value="">Unassigned</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.fullName}</option>)}
              </select>
            </div>
          </div>
          <div className="row5" style={{ marginBottom: 9 }}>
            <div className="fg"><label>Loan number *</label><input value={form.loanNumber} onChange={e => set('loanNumber', e.target.value)} placeholder="Alpha-numeric" /></div>
            <div className="fg"><label>Loan amount</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: 8, color: '#888', fontSize: 13 }}>$</span>
                <input type="number" value={form.loanAmount} onChange={e => set('loanAmount', e.target.value)} placeholder="0" style={{ paddingLeft: 22 }} />
              </div>
            </div>
            <div className="fg"><label>Loan type</label>
              <select value={form.loanTypeId} onChange={e => set('loanTypeId', e.target.value)}>
                <option value="">Select...</option>
                {activeLoanTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="fg"><label>Loan program</label><input value={form.loanProgram} onChange={e => set('loanProgram', e.target.value)} placeholder="e.g. FHA" /></div>
            <div className="fg"><label>Property type</label><input value={form.propertyType} onChange={e => set('propertyType', e.target.value)} placeholder="e.g. Single family" /></div>
          </div>
          <div className="row5">
            <div className="fg"><label>Lender *</label><input value={form.lender} onChange={e => set('lender', e.target.value)} placeholder="Bank name" /></div>
            <div className="fg"><label>Broker</label><input value={form.broker} onChange={e => set('broker', e.target.value)} /></div>
            <div className="fg"><label>Client contact</label><input value={form.clientContact} onChange={e => set('clientContact', e.target.value)} /></div>
            <div className="fg"><label>Orig system</label>
              <select value={form.origSystemId} onChange={e => set('origSystemId', e.target.value)}>
                <option value="">Select...</option>
                {lookups.origSystems.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="fg"><label>Closing date</label><input type="date" value={form.closingDate} onChange={e => set('closingDate', e.target.value)} /></div>
          </div>

          {isLoan && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #eee' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Loan details</div>
              <div className="row5">
                <div className="fg"><label>Rate (%)</label><input value={form.rate} onChange={e => set('rate', e.target.value)} placeholder="e.g. 6.75" /></div>
                <div className="fg"><label>Jumbo</label>
                  <select value={form.jumbo} onChange={e => set('jumbo', e.target.value)}>
                    <option value="">Select...</option><option>Yes</option><option>No</option>
                  </select>
                </div>
                <div className="fg"><label>MI</label>
                  <select value={form.mi} onChange={e => set('mi', e.target.value)}>
                    <option value="">Select...</option><option>Yes</option><option>No</option>
                  </select>
                </div>
                <div className="fg"><label>Lien status</label>
                  <select value={form.lienStatusId} onChange={e => set('lienStatusId', e.target.value)}>
                    <option value="">Select...</option>
                    {lookups.lienStatuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="fg"><label>Purpose</label><input value={form.purpose} onChange={e => set('purpose', e.target.value)} placeholder="e.g. Purchase" /></div>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', alignItems: 'start' }}>
          {/* Borrowers */}
          <div className="card" style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div className="section-hdr" style={{ margin: 0, border: 'none', padding: 0 }}>Borrowers</div>
              <button className="btn btn-sm" onClick={addBorrower}>+ Add borrower</button>
            </div>
            {borrowers.map((b, i) => (
              <div key={i} className="borrower-row">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', whiteSpace: 'nowrap', minWidth: 68 }}>Borrower {i + 1}</div>
                  <div style={{ flex: 1, minWidth: 100 }}><label style={{ marginBottom: 2 }}>First name *</label><input value={b.firstName} onChange={e => updateBorrower(i, 'firstName', e.target.value)} placeholder="Jane" /></div>
                  <div style={{ flex: 1, minWidth: 100 }}><label style={{ marginBottom: 2 }}>Last name *</label><input value={b.lastName} onChange={e => updateBorrower(i, 'lastName', e.target.value)} placeholder="Smith" /></div>
                  <div style={{ flex: 1, minWidth: 100 }}><label style={{ marginBottom: 2 }}>Phone</label><input type="tel" value={b.phone} onChange={e => updateBorrower(i, 'phone', e.target.value)} placeholder="(555) 000-0000" /></div>
                  <div style={{ flex: 1.5, minWidth: 130 }}><label style={{ marginBottom: 2 }}>Email</label><input type="email" value={b.email} onChange={e => updateBorrower(i, 'email', e.target.value)} placeholder="name@email.com" /></div>
                  {borrowers.length > 1 && <button className="btn-ghost" onClick={() => removeBorrower(i)} style={{ marginTop: 14, flexShrink: 0 }} title="Remove"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg></button>}
                </div>
              </div>
            ))}
          </div>

          {/* Property */}
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="section-hdr">Property address</div>
            <div className="fg"><label>Street address</label><input value={form.street} onChange={e => set('street', e.target.value)} placeholder="123 Main St" /></div>
            <div className="row3" style={{ marginTop: 8 }}>
              <div className="fg"><label>City</label><input value={form.city} onChange={e => set('city', e.target.value)} /></div>
              <div className="fg"><label>State</label><input value={form.state} onChange={e => set('state', e.target.value.toUpperCase())} maxLength={2} placeholder="KS" /></div>
              <div className="fg"><label>ZIP</label><input value={form.zip} onChange={e => set('zip', e.target.value)} placeholder="66000" /></div>
            </div>
          </div>

          {/* Revenue + Submit */}
          <div style={{ marginBottom: 0 }}>
            <div className="card">
              <div className="section-hdr">Revenue</div>
              <div className="fg"><label>Revenue</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: 8, color: '#888', fontSize: 13 }}>$</span>
                  <input type="number" value={form.revenue} onChange={e => set('revenue', e.target.value)} placeholder="0.00" style={{ paddingLeft: 22 }} />
                </div>
              </div>
              <div className="fg"><label>Revenue received</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <input type="checkbox" checked={form.revReceived} onChange={e => set('revReceived', e.target.checked)} />
                  <input type="date" value={form.revDate} onChange={e => set('revDate', e.target.value)} disabled={!form.revReceived} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Creating...' : 'Create file'}
              </button>
              <button className="btn" onClick={() => navigate(-1)}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
